import styled, { keyframes } from "styled-components";

/**
 * Animação para expandir o conteúdo.
 */
const slideDown = keyframes`
  from {
    max-height: 0;
  }
  to {
    max-height: 500px;
  }
`;

/**
 * Animação para recolher o conteúdo.
 */
const slideUp = keyframes`
  from {
    max-height: 500px;
  }
  to {
    max-height: 0;
  }
`;

/**
 * Animação para exibir o conteúdo.
 */
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/**
 * Animação para ocultar o conteúdo.
 */
const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

/**
 * Wrapper Principal do Accordion
 * Configura o layout principal do componente.
 */
export const AccordionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

/**
 * Wrapper do Item do Accordion
 * Configura o layout e estilos do item expansível.
 */
export const AccordionItemWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isOpen"].includes(prop),
})<{ isOpen: boolean }>`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: none;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
`;

/**
 * Cabeçalho do Item
 * Configura o título e o ícone clicável.
 */
export const AccordionHeader = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isOpen"].includes(prop),
})<{ isOpen: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  min-height: 3.5rem;
  background-color: ${({ theme }) => theme.colors.vividBlue};
  border-radius: ${({ theme, isOpen }) =>
    isOpen
      ? `${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0`
      : theme.borderRadius.medium};
  cursor: pointer;
  box-shadow: none;
  transition: box-shadow 0.3s ease-in-out;

  h3 {
    margin: 0;
    color: ${({ theme }) => theme.colors.white};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    text-align: left;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing(2.5)};
  }
`;

/**
 * Ícone do Cabeçalho
 * Exibe o ícone de expansão ou recolhimento.
 */
export const AccordionIcon = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  display: inline-flex;
  color: ${({ theme }) => theme.colors.white};
`;

/**
 * Conteúdo do Item
 * Configura o layout do conteúdo expansível.
 */
export const AccordionContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isOpen"].includes(prop),
})<{ isOpen: boolean }>`
  padding: ${({ isOpen, theme }) =>
    isOpen ? theme.spacing(4) : theme.spacing(0)};
  background-color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.black};
  max-height: ${({ isOpen }) => (isOpen ? "500px" : "0")};
  overflow: hidden;
  animation: ${({ isOpen }) => (isOpen ? slideDown : slideUp)} 0.3s ease-in-out;
  transition: padding 0.3s ease-in-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ isOpen, theme }) =>
      isOpen ? theme.spacing(2.5) : theme.spacing(0)};
  }

  & > p {
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    animation: ${({ isOpen }) => (isOpen ? fadeIn : fadeOut)} 0.3s ease-in-out;
    transition: opacity 0.3s ease-in-out;
    white-space: pre-line;
    line-height: 1.55;
  }
`;
